/**
 * celebrateRow — fire the warm-amber sparkle on a row identified by its
 * `data-testid`.
 *
 * Implementation note (why we float on document.body):
 *   1. React owns row className via JSX, so `classList.add()` gets wiped
 *      on the next re-render.
 *   2. Appending the overlay as a child of the row works UNTIL React
 *      filters the row out of the DOM (e.g. TasksView's "Today" tab
 *      excludes COMPLETED items, so the row disappears mid-animation).
 *   3. The fix: capture the row's `getBoundingClientRect()` synchronously
 *      RIGHT NOW (before React's optimistic re-render commits) and
 *      append a `position: fixed` overlay to document.body. The overlay
 *      is decoupled from React's tree entirely — it runs the animation
 *      to completion regardless of what happens to the source row.
 *
 * Works for every completion path in the app:
 *   - Today's DecisionQueue (row stays visible, overlay tracks it)
 *   - TasksView (row gets filtered out — overlay finishes anyway)
 *   - Calls / Payments / Reading (cards stay visible)
 *   - Bulk variants via `celebrateRows`
 *
 * Safe in SSR (window guard) and reduced-motion (the CSS keyframe is
 * gated by `prefers-reduced-motion: reduce` in aesthetic.css).
 */
const CELEBRATION_MS = 1200;

export function celebrateRow(testId) {
  if (typeof window === 'undefined' || !testId) return;
  // CAPTURE synchronously — must run BEFORE React's optimistic setState
  // triggers a re-render that could unmount the row. Callers should
  // invoke celebrateRow *before* their setState, but either order works
  // because React batches state updates inside event handlers and
  // doesn't actually commit DOM changes until the handler returns.
  const el = document.querySelector(`[data-testid="${testId}"]`);
  if (!el) return;

  const rect = el.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return;

  const computed = window.getComputedStyle(el);
  const overlay = document.createElement('div');
  overlay.className = 'pd-celebrate-overlay';
  overlay.setAttribute('aria-hidden', 'true');
  Object.assign(overlay.style, {
    position: 'fixed',
    top: `${rect.top}px`,
    left: `${rect.left}px`,
    width: `${rect.width}px`,
    height: `${rect.height}px`,
    // Preserve the row's corners so the wash hugs the same shape.
    borderTopLeftRadius: computed.borderTopLeftRadius,
    borderTopRightRadius: computed.borderTopRightRadius,
    borderBottomLeftRadius: computed.borderBottomLeftRadius,
    borderBottomRightRadius: computed.borderBottomRightRadius,
  });
  document.body.appendChild(overlay);

  window.setTimeout(() => {
    overlay.remove();
  }, CELEBRATION_MS);
}

export function celebrateRows(testIds) {
  if (!Array.isArray(testIds)) return;
  // Stagger 60ms across rows so a bulk-complete reads as a musical cascade.
  testIds.forEach((id, i) => {
    window.setTimeout(() => celebrateRow(id), i * 60);
  });
}
